import { Box, Typography } from '@mui/material';

export type JourneyQuoteInsuredHeaderProps = {
  /** Nome (ou nome social) do segurado. */
  name?: string | null;
  /** CPF do segurado em qualquer formato (somente dígitos ou já mascarado). */
  document?: string | null;
  /** Rótulo do equipamento já traduzido (ex.: "Ciclomotor e Moto Elétrica"). */
  equipmentLabel?: string | null;
  /** Valor monetário do equipamento. */
  equipmentValue?: number | null;
};

const formatCpf = (raw: string) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return raw;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { currency: 'BRL', minimumFractionDigits: 2, style: 'currency' }).format(value);

/**
 * Cabeçalho de identificação do segurado renderizado acima do conteúdo da jornada de cotação.
 *
 * Especificação Figma (node-id 599:8904): duas linhas com dados do segurado e do equipamento,
 * exibidas alinhadas à esquerda, abaixo do stepper e acima do título "Proteja seu...".
 */
export default function JourneyQuoteInsuredHeader({
  name,
  document,
  equipmentLabel,
  equipmentValue
}: JourneyQuoteInsuredHeaderProps) {
  const trimmedName = name?.trim() || null;
  const trimmedDocument = document?.trim() || null;
  const trimmedEquipmentLabel = equipmentLabel?.trim() || null;
  const hasEquipmentValue = typeof equipmentValue === 'number' && Number.isFinite(equipmentValue);

  const hasFirstRow = !!trimmedName || !!trimmedDocument;
  const hasSecondRow = !!trimmedEquipmentLabel || hasEquipmentValue;

  if (!hasFirstRow && !hasSecondRow) return null;

  return (
    <Box
      data-app-id="journey-quote-insured-header"
      sx={{
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: '100%'
      }}
    >
      {hasFirstRow ? (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {trimmedName ? (
            <Typography component="span" sx={{ fontWeight: 500 }} variant="subtitle1">
              {trimmedName}
            </Typography>
          ) : null}
          {trimmedDocument ? (
            <Typography component="span" variant="body1">
              CPF: {formatCpf(trimmedDocument)}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {hasSecondRow ? (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {trimmedEquipmentLabel ? (
            <Typography component="span" sx={{ fontWeight: 500 }} variant="subtitle1">
              {trimmedEquipmentLabel}
            </Typography>
          ) : null}
          {hasEquipmentValue ? (
            <Typography component="span" variant="body1">
              {formatCurrency(equipmentValue as number)}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
